import {
	AutoFactory,
	OnlinePortalAdobeEsignApi,
	DynamicApplicationAdobePhoneAuthenticationSettingsApi,
	DynamicApplicationSettingValue,
	OnlinePortalLoanApplicationsApi,
	AgreementStatus,
	RecipientSignatureStatus,
	LoanApplication,
	LoanApplicationApplicant,
	OnlinePortalDocusignApi,
	DynamicApplicationDocusignPhoneAuthenticationSettingsApi,
	DynamicApplicationThirdPartySettingsApi
} from '@sageworks/jpi';

import Vue from 'vue';
import { FileHelper } from '@sageworks/core-logic';
import { defineModule, localActionContext } from 'direct-vuex';
import { LoanRoleType } from '@sageworks/dynamic-forms';
import { SignatureType } from '@sageworks/core-logic/src/enums/signature-type';

// eslint-disable-next-line no-use-before-define
const actionContext = (context: any) => localActionContext(context, ESignModule);

export interface AdobeESignModuleState {
	customerCanUseEsign: boolean;
	customerHasPhoneAuthEnabled: false;
	esignAgreementStatus: AgreementStatus;
	applicationHasAdobeAgreement: boolean;
	applicationRecipientInformation: ApplicationRecipientInformation[];
	esignTemplateStatuses: Map<number, string>;
	signatureType: SignatureType | undefined;
	signerMap: Map<number, boolean>;
	templateInitiatorMap: Map<number, boolean>;
	templateInitiatorEmailMap: Map<number, string>;
	showDocusignTemplatesInApplication: boolean;
}

export interface ApplicationRecipientInformation {
	role: string | '';
	name: string | '';
	email: string | '';
}

const ESignModule = defineModule({
	namespaced: true,

	state: () => {
		return {
			customerCanUseEsign: false,
			customerHasPhoneAuthEnabled: false,
			applicationHasAdobeAgreement: false,
			esignAgreementStatus: {},
			esignTemplateStatuses: new Map<number, string>(),
			signerMap: new Map<number, boolean>(),
			templateInitiatorMap: new Map<number, boolean>(),
			templateInitiatorEmailMap: new Map<number, string>(),
			signatureType: undefined,
			showDocusignTemplatesInApplication: false
		} as AdobeESignModuleState;
	},
	mutations: {
		SET_USER_CAN_USE_ESIGN(state, customerCanUseEsign: boolean) {
			Vue.set(state, 'customerCanUseEsign', customerCanUseEsign);
		},
		SET_SHOW_DOCUSIGN_TEMPLATES_IN_APPLICATION(state, showDocusignTemplatesInApplication: boolean) {
			Vue.set(state, 'showDocusignTemplatesInApplication', showDocusignTemplatesInApplication);
		},
		SET_ESIGN_AGREEMENT_STATUS(state, esignAgreementStatus: AgreementStatus) {
			Vue.set(state, 'esignAgreementStatus', esignAgreementStatus);
		},
		SET_APPLICATION_HAS_ADOBE_AGREEMENT(state, applicationHasAdobeAgreement: boolean) {
			state.applicationHasAdobeAgreement = applicationHasAdobeAgreement;
		},
		SET_USER_PHONE_AUTH_ENABLED(state, customerHasPhoneAuthEnabled: boolean) {
			Vue.set(state, 'customerHasPhoneAuthEnabled', customerHasPhoneAuthEnabled);
		},
		SET_APPLICATION_RECIPIENT_INFORMATION(state, loanApplicationApplicants: LoanApplicationApplicant[]) {
			var applicationRecipientInformation: ApplicationRecipientInformation[] = [];

			loanApplicationApplicants.forEach(loanApplicationApplicant => {
				var role = '';
				if (loanApplicationApplicant.roles) {
					// We should never have a borrower with multiple roles... Grabbing the first one of the list will suffice
					var borrowerType = loanApplicationApplicant.roles.length > 0 ? loanApplicationApplicant.roles[0] : '';

					switch (borrowerType) {
						case LoanApplicationApplicant.RolesEnum.PrimaryBorrower:
							role = LoanRoleType.PrimaryBorrower;
							break;
						case LoanApplicationApplicant.RolesEnum.CoBorrower:
							role = LoanRoleType.CoBorrower;
							break;
						case LoanApplicationApplicant.RolesEnum.Guarantor:
							role = LoanRoleType.Guarantor;
							break;
						case LoanApplicationApplicant.RolesEnum.CoSigner:
							role = LoanRoleType.CoSigner;
							break;
					}
				}

				applicationRecipientInformation.push({
					name: loanApplicationApplicant.name ?? '',
					email: loanApplicationApplicant.email ?? '',
					role: role
				});
			});

			state.applicationRecipientInformation = applicationRecipientInformation;
		},
		SET_ESIGN_TEMPLATE_STATUSES(state, { documentRequestId, status }: { documentRequestId: number; status: string }) {
			state.esignTemplateStatuses.set(documentRequestId, status);
			state.esignTemplateStatuses = new Map(state.esignTemplateStatuses.entries());
		},
		SET_SIGNATURE_TYPE(state, signatureType: SignatureType) {
			state.signatureType = signatureType;
		},
		SET_SIGNER_MAP(state, { documentRequestId, isCurrentUserSigner }: { documentRequestId: number; isCurrentUserSigner: boolean }) {
			state.signerMap.set(documentRequestId, isCurrentUserSigner);
			state.signerMap = new Map(state.signerMap.entries());
		},
		SET_TEMPLATE_INITIATOR_MAP(
			state,
			{ documentRequestId, isCurrentUserTemplateInitiator }: { documentRequestId: number; isCurrentUserTemplateInitiator: boolean }
		) {
			state.templateInitiatorMap.set(documentRequestId, isCurrentUserTemplateInitiator);
			state.templateInitiatorMap = new Map(state.templateInitiatorMap.entries());
		},
		SET_TEMPLATE_INITIATOR_EMAIL_MAP(state, { documentRequestId, initiatorEmail }: { documentRequestId: number; initiatorEmail: string }) {
			state.templateInitiatorEmailMap.set(documentRequestId, initiatorEmail);
			state.templateInitiatorEmailMap = new Map(state.templateInitiatorEmailMap.entries());
		},
		INVALIDATE_TEMPLATE_INITIATOR_MAP(state, { documentRequestId }: { documentRequestId: number }) {
			state.templateInitiatorMap.delete(documentRequestId);
			state.templateInitiatorMap = new Map(state.templateInitiatorMap.entries());
		},
	},
	actions: {
		async getUserCanUseEsign(context, { applicationType }): Promise<void> {
			const { commit, state, dispatch } = actionContext(context);

			try {
				// Ensure signatureType is set before making the API call
				if (state.signatureType == null) {
					await dispatch.fetchSignatureType({ applicationType });
				}

				const esignService =
					state.signatureType === SignatureType.DOCUSIGN ? AutoFactory.get(OnlinePortalDocusignApi) : AutoFactory.get(OnlinePortalAdobeEsignApi);
				const customerCanUseEsign = await esignService.getCustomerCanUseEsign(applicationType);

				commit.SET_USER_CAN_USE_ESIGN(customerCanUseEsign);
			} catch (error) {
				// If the endpoint fails, default to false
				commit.SET_USER_CAN_USE_ESIGN(false);
			}
		},
		async getShowDocusignTemplatesInApplication(context, { applicationType }): Promise<void> {
			const { commit } = actionContext(context);

			// S2 flag is ALWAYS fetched from Docusign API, regardless of signature type
			try {
				const docusignService = AutoFactory.get(OnlinePortalDocusignApi);
				const showDocusignTemplatesInApplication = await docusignService.getShowDocusignTemplatesInApplication(
					applicationType
				);
				commit.SET_SHOW_DOCUSIGN_TEMPLATES_IN_APPLICATION(showDocusignTemplatesInApplication);
			} catch (error) {
				// If the endpoint fails, default to false
				commit.SET_SHOW_DOCUSIGN_TEMPLATES_IN_APPLICATION(false);
			}
		},
		async getUserPhoneAuthEnabled(context, { applicationType, signatureType }): Promise<void> {
			const { dispatch } = actionContext(context);
			if (signatureType === SignatureType.DOCUSIGN) {
				return await dispatch.getUserPhoneAuthEnabledForDocusign(applicationType);
			} else {
				return await dispatch.getUserPhoneAuthEnabledForAdobe(applicationType);
			}
		},
		async getUserPhoneAuthEnabledForAdobe(context, applicationType: LoanApplication.TypeEnum): Promise<void> {
			const { commit } = actionContext(context);

			const adobeAuthService = AutoFactory.get(DynamicApplicationAdobePhoneAuthenticationSettingsApi);
			const phoneAuthSettings = await adobeAuthService.getAll();
			var phoneAuthEnabled = false;
			const dynAppSetting = DynamicApplicationSettingValue.DynamicApplicationSettingEnum;
			switch (applicationType) {
				case LoanApplication.TypeEnum.Personal:
					phoneAuthEnabled =
						phoneAuthSettings.items?.find(s => s.dynamicApplicationSetting === dynAppSetting.PersonAppsAdobeAuthenticationMethod)?.settingValue ??
						false;
					break;
				case LoanApplication.TypeEnum.Business:
					phoneAuthEnabled =
						phoneAuthSettings.items?.find(s => s.dynamicApplicationSetting === dynAppSetting.BusinessAppsAdobeAuthenticationMethod)?.settingValue ??
						false;
					break;
				case LoanApplication.TypeEnum.Nonprofit:
					phoneAuthEnabled =
						phoneAuthSettings.items?.find(s => s.dynamicApplicationSetting === dynAppSetting.NonprofitAppsAdobeAuthenticationMethod)
							?.settingValue ?? false;
					break;
				case LoanApplication.TypeEnum.Sba:
					phoneAuthEnabled =
						phoneAuthSettings.items?.find(s => s.dynamicApplicationSetting === dynAppSetting.SbaAppsAdobeAuthenticationMethod)?.settingValue ??
						false;
					break;
			}

			commit.SET_USER_PHONE_AUTH_ENABLED(phoneAuthEnabled);
		},
		async getUserPhoneAuthEnabledForDocusign(context, applicationType: LoanApplication.TypeEnum): Promise<void> {
			const { commit } = actionContext(context);

			const docusignAuthService = AutoFactory.get(DynamicApplicationDocusignPhoneAuthenticationSettingsApi);
			const phoneAuthSettings = await docusignAuthService.getAll();
			var phoneAuthEnabled = false;
			const dynAppSetting = DynamicApplicationSettingValue.DynamicApplicationSettingEnum;
			switch (applicationType) {
				case LoanApplication.TypeEnum.Personal:
					phoneAuthEnabled =
						phoneAuthSettings.items?.find(s => s.dynamicApplicationSetting === dynAppSetting.PersonAppsDocusignAuthenticationMethod)
							?.settingValue ?? false;
					break;
				case LoanApplication.TypeEnum.Business:
					phoneAuthEnabled =
						phoneAuthSettings.items?.find(s => s.dynamicApplicationSetting === dynAppSetting.BusinessAppsDocusignAuthenticationMethod)
							?.settingValue ?? false;
					break;
				case LoanApplication.TypeEnum.Nonprofit:
					phoneAuthEnabled =
						phoneAuthSettings.items?.find(s => s.dynamicApplicationSetting === dynAppSetting.NonprofitAppsDocusignAuthenticationMethod)
							?.settingValue ?? false;
					break;
				case LoanApplication.TypeEnum.Sba:
					phoneAuthEnabled =
						phoneAuthSettings.items?.find(s => s.dynamicApplicationSetting === dynAppSetting.SbaAppsDocusignAuthenticationMethod)?.settingValue ??
						false;
					break;
			}

			commit.SET_USER_PHONE_AUTH_ENABLED(phoneAuthEnabled);
		},
		async convertApplicationHtmlToPdf(context, { loanApplicationNumber, html }): Promise<void> {
			const { dispatch } = actionContext(context);
			const loanApplicationService = AutoFactory.get(OnlinePortalLoanApplicationsApi);
			const exportReturn = await loanApplicationService.convertApplicationHtmlToPdf(html);

			dispatch.downloadExportFile({ loanApplicationNumber, exportReturn });
		},
		async createApplicationExportPdf(context, { loanApplicationNumber, lenderOnlyMode }): Promise<void> {
			const { dispatch } = actionContext(context);
			const loanApplicationService = AutoFactory.get(OnlinePortalLoanApplicationsApi);
			const onlinePortalApplicationExportDto = { loanApplicationId: loanApplicationNumber, lenderOnlyMode: lenderOnlyMode };
			const exportReturn = await loanApplicationService.createApplicationExportPdf(onlinePortalApplicationExportDto);

			dispatch.downloadExportFile({ loanApplicationNumber, exportReturn });
		},
		downloadExportFile(context, { loanApplicationNumber, exportReturn }) {
			if (exportReturn.content) {
				const base64Content = FileHelper.getBase64FromDataUri(exportReturn.content);
				const mimeType = FileHelper.getMimeTypeFromDataUri(exportReturn.content);
				FileHelper.downloadFile(base64Content, `Loan_Application_${loanApplicationNumber}.pdf`, mimeType);
			}
		},
		async sendApplicationEmailsForSignatures(context, { applicationId, html }): Promise<void> {
			const { dispatch, state } = actionContext(context);
			if (state.signatureType === SignatureType.DOCUSIGN) {
				return await dispatch.sendApplicationEmailsForSignaturesForDocusign({ applicationId, html });
			} else {
				return await dispatch.sendApplicationEmailsForSignaturesForAdobe({ applicationId, html });
			}
		},
		async sendApplicationEmailsForSignaturesForAdobe(context, { applicationId, html }): Promise<any> {
			const esignService = AutoFactory.get(OnlinePortalAdobeEsignApi);
			try {
				const response = await esignService.sendApplicationEmailSignatureRequests(applicationId, html);
				return response;
			} catch (error: any) {
				// Check if the error response is the phone number invalid string
				if (error?.response === 'PHONE_NUMBER_INVALID') {
					return {
						error: true,
						errorCode: 'PHONE_NUMBER_INVALID',
						message: 'Phone number is not valid.',
						userMessage: 'The Phone Number provided for the applicant(s) is invalid. Please review and correct, and then try again. ' +
							'For further assistance, please contact Abrigo Support.'
					};
				}

				// Check if the error response contains a message from the backend (e.g., private message error)
				if (error?.response && typeof error.response === 'string') {
					return {
						error: true,
						errorMessage: error.response,
						userMessage: error.response
					};
				}

				// Re-throw other errors to be handled by the calling component
				throw error;
			}
		},
		async sendApplicationEmailsForSignaturesForDocusign(context, { applicationId, html }): Promise<any> {
			const esignService = AutoFactory.get(OnlinePortalDocusignApi);
			try {
				const response = await esignService.sendApplicationEmailSignatureRequestsAsync(applicationId, html);
				return response;
			} catch (error: any) {
				// Check if the error response is the phone number invalid string
				if (error?.response === 'PHONE_NUMBER_INVALID') {
					return {
						error: true,
						errorCode: 'PHONE_NUMBER_INVALID',
						message: 'Phone number is not valid.',
						userMessage: 'The Phone Number provided for the applicant(s) is invalid. Please review and correct, and then try again. ' +
							'For further assistance, please contact Abrigo Support.'
					};
				}

				// Re-throw other errors to be handled by the calling component
				throw error;
			}
		},
		async resendApplicationEmailsForSignatures(context, { applicationId, html }): Promise<any> {
			const { dispatch, state } = actionContext(context);
			if (state.signatureType === SignatureType.DOCUSIGN) {
				return await dispatch.resendApplicationEmailForSignaturesForDocusign({ applicationId, html });
			} else {
				return await dispatch.resendApplicationEmailsForSignaturesForAdobe({ applicationId, html });
			}
		},
		async resendApplicationEmailsForSignaturesForAdobe(context, { applicationId, html }): Promise<any> {
			const esignService = AutoFactory.get(OnlinePortalAdobeEsignApi);
			try {
				const response = await esignService.resendApplicationEmailSignatureRequests(applicationId, html);
				return response;
			} catch (error: any) {
				// Check if the error response is the phone number invalid string
				if (error?.response === 'PHONE_NUMBER_INVALID') {
					return {
						error: true,
						errorCode: 'PHONE_NUMBER_INVALID',
						message: 'Phone number is not valid.',
						userMessage: 'The Phone Number provided for the applicant(s) is invalid. Please review and correct, and then try again. ' +
							'For further assistance, please contact Abrigo Support.'
					};
				}

				// Check if the error response contains a message from the backend (e.g., private message error)
				if (error?.response && typeof error.response === 'string') {
					return {
						error: true,
						errorMessage: error.response,
						userMessage: error.response
					};
				}

				// Re-throw other errors to be handled by the calling component
				throw error;
			}
		},
		async resendApplicationEmailForSignaturesForDocusign(context, { applicationId, html }): Promise<any> {
			const esignService = AutoFactory.get(OnlinePortalDocusignApi);
			try {
				const response = await esignService.resendApplicationEmailSignatureRequestsAsync(applicationId, html);
				return response;
			} catch (error: any) {
				// Check if the error response is the phone number invalid string
				if (error?.response === 'PHONE_NUMBER_INVALID') {
					return {
						error: true,
						errorCode: 'PHONE_NUMBER_INVALID',
						message: 'Phone number is not valid.',
						userMessage: 'The Phone Number provided for the applicant(s) is invalid. Please review and correct, and then try again. ' +
							'For further assistance, please contact Abrigo Support.'
					};
				}

				// Re-throw other errors to be handled by the calling component
				throw error;
			}
		},
		async fetchLoanApplicationRecipientStatuses(context, { applicationId }): Promise<void> {
			const { dispatch, state } = actionContext(context);
			if (state.signatureType === SignatureType.DOCUSIGN) {
				return await dispatch.fetchLoanApplicationRecipientStatusForDocusign({ applicationId });
			} else {
				return await dispatch.fetchLoanApplicationRecipientStatusesForAdobe({ applicationId });
			}
		},
		async fetchLoanApplicationRecipientStatusesForAdobe(context, { applicationId }): Promise<void> {
			const { commit } = actionContext(context);

			const esignService = AutoFactory.get(OnlinePortalAdobeEsignApi);
			var agreementStatus: AgreementStatus = await esignService.getApplicationRecipientStatuses(applicationId);
			commit.SET_ESIGN_AGREEMENT_STATUS(agreementStatus);
		},
		async fetchLoanApplicationRecipientStatusForDocusign(context, { applicationId }): Promise<void> {
			const { commit } = actionContext(context);

			const esignService = AutoFactory.get(OnlinePortalDocusignApi);
			var agreementStatus: AgreementStatus = await esignService.getApplicationRecipientStatus(applicationId);
			commit.SET_ESIGN_AGREEMENT_STATUS(agreementStatus);
		},
		async checkIfApplicationHasAdobeAgreement(context, { applicationId }): Promise<void> {
			const { commit } = actionContext(context);

			const esignService = AutoFactory.get(OnlinePortalAdobeEsignApi);
			var applicationHasAdobeAgreement: boolean = await esignService.applicationHasAgreement(applicationId);

			commit.SET_APPLICATION_HAS_ADOBE_AGREEMENT(applicationHasAdobeAgreement);
		},
		async fetchLoanApplicationRecipientInformation(context, { applicationId }): Promise<void> {
			const { commit, state } = actionContext(context);
			if (state.signatureType === SignatureType.DOCUSIGN) {
				const esignService = AutoFactory.get(OnlinePortalDocusignApi);
				var docusignLoanAppApplicants = await esignService.getApplicationRecipientInformation(applicationId);
				commit.SET_APPLICATION_RECIPIENT_INFORMATION(docusignLoanAppApplicants);
			} else {
				const esignService = AutoFactory.get(OnlinePortalAdobeEsignApi);
				var loanApplicationApplicants: LoanApplicationApplicant[] = await esignService.getApplicationRecipientInformation(applicationId);
				commit.SET_APPLICATION_RECIPIENT_INFORMATION(loanApplicationApplicants);
			}
		},
		async sendTemplatesForSignature(
			context,
			{ adobeTemplateDocumentNameMappingId, docusignTemplateDocumentNameMappingId, customerId, documentRequestId }
		): Promise<any> {
			const { dispatch, state } = actionContext(context);
			if (state.signatureType === SignatureType.DOCUSIGN) {
				return await dispatch.sendDocusignTemplatesForSignature({ docusignTemplateDocumentNameMappingId, customerId, documentRequestId });
			} else {
				return await dispatch.sendAdobeTemplatesForSignature({ adobeTemplateDocumentNameMappingId, customerId, documentRequestId });
			}
		},
		async sendAdobeTemplatesForSignature(context, { adobeTemplateDocumentNameMappingId, customerId, documentRequestId }): Promise<any> {
			const esignService = AutoFactory.get(OnlinePortalAdobeEsignApi);
			try {
				const response = await esignService.sendAdobeTemplatesForSignature({ adobeTemplateDocumentNameMappingId, customerId, documentRequestId });
				return response;
			} catch (error: any) {
				// Check if the error response contains an error message from the backend
				if (error?.response) {
					return {
						error: true,
						errorMessage: error.response,
						userMessage: error.response
					};
				}

				// Re-throw other errors to be handled by the calling component
				throw error;
			}
		},
		async sendDocusignTemplatesForSignature(context, { docusignTemplateDocumentNameMappingId, customerId, documentRequestId }): Promise<any> {
			const docusignService = AutoFactory.get(OnlinePortalDocusignApi);
			try {
				const response = await docusignService.sendDocusignTemplatesForSignatureAsync({
					docusignTemplateDocumentNameMappingId,
					customerId,
					documentRequestId
				});
				return response;
			} catch (error: any) {
				// Handle specific Docusign errors
				if (error?.response?.errorCode === 'PHONE_NUMBER_INVALID') {
					return {
						error: true,
						errorCode: 'PHONE_NUMBER_INVALID',
						message: error.response.message || 'Phone number is not valid.',
						userMessage: 'The Phone Number provided for the applicant(s) is invalid. Please review and correct, and then try again. ' +
							'For further assistance, please contact Abrigo Support.'
					};
				}

				// Re-throw other errors to be handled by the calling component
				throw error;
			}
		},
		async getSignerMap(context, { documentRequestId, applicationType }): Promise<boolean> {
			const { commit, state, dispatch } = actionContext(context);

			if (state.signerMap.has(documentRequestId)) {
				const cachedValue = state.signerMap.get(documentRequestId) || false;
				return cachedValue;
			}

			// Ensure signature type is fetched before checking signer status
			if (state.signatureType == null && applicationType) {
				await dispatch.fetchSignatureType({ applicationType });
			}

			let isCurrentUserSigner = false;
			if (state.signatureType === SignatureType.DOCUSIGN) {
				const esignService = AutoFactory.get(OnlinePortalDocusignApi);
				const response = await esignService.getIsCurrentUserSigner({ documentRequestId });
				isCurrentUserSigner = response.isCurrentUserSigner ?? false;
			} else {
				const esignService = AutoFactory.get(OnlinePortalAdobeEsignApi);
				const response = await esignService.getIsCurrentUserSigner({ documentRequestId });
				isCurrentUserSigner = response.isCurrentUserSigner ?? false;
			}
			commit.SET_SIGNER_MAP({ documentRequestId, isCurrentUserSigner: isCurrentUserSigner });
			return isCurrentUserSigner;
		},
		async getTemplateInitiatorMap(context, { documentRequestId, applicationType }): Promise<boolean> {
			const { commit, state, dispatch } = actionContext(context);
			if (state.templateInitiatorMap.has(documentRequestId)) {
				return state.templateInitiatorMap.get(documentRequestId) || false;
			}

			// Ensure signature type is fetched before checking template initiator status
			if (state.signatureType == null && applicationType) {
				await dispatch.fetchSignatureType({ applicationType });
			}

			let isCurrentUserTemplateInitiator = false;
			let initiatorEmail = '';

			if (state.signatureType === SignatureType.DOCUSIGN) {
				const esignService = AutoFactory.get(OnlinePortalDocusignApi);
				const response = await esignService.isCurrentUserTemplateInitiator({ documentRequestId });
				isCurrentUserTemplateInitiator = response.isCurrentUserTemplateInitiator ?? false;
				initiatorEmail = response.templateInitiatorEmail ?? '';
			} else {
				const esignService = AutoFactory.get(OnlinePortalAdobeEsignApi);
				const response = await esignService.isCurrentUserTemplateInitiator({ documentRequestId });
				isCurrentUserTemplateInitiator = response.isCurrentUserTemplateInitiator ?? false;
				initiatorEmail = response.templateInitiatorEmail ?? '';
			}

			commit.SET_TEMPLATE_INITIATOR_MAP({ documentRequestId, isCurrentUserTemplateInitiator: isCurrentUserTemplateInitiator });
			if (initiatorEmail) {
				commit.SET_TEMPLATE_INITIATOR_EMAIL_MAP({ documentRequestId, initiatorEmail });
			}

			return isCurrentUserTemplateInitiator;
		},
		async invalidateTemplateInitiatorCache(context, { documentRequestId }): Promise<void> {
			const { commit } = actionContext(context);
			commit.INVALIDATE_TEMPLATE_INITIATOR_MAP({ documentRequestId });
		},
		async fetchTemplateStatus(context, { documentRequestId, documentNamesId }): Promise<string> {
			const { dispatch, state } = actionContext(context);
			if (state.signatureType === SignatureType.DOCUSIGN) {
				return await dispatch.fetchDocusignTemplateStatus({ documentRequestId, documentNamesId });
			} else {
				return await dispatch.fetchAdobeTemplateStatus({ documentRequestId, documentNamesId });
			}
		},
		async fetchAdobeTemplateStatus(context, { documentRequestId, documentNamesId }): Promise<string> {
			const { commit } = actionContext(context);
			const esignService = AutoFactory.get(OnlinePortalAdobeEsignApi);
			const response = await esignService.fetchTemplateStatus({ documentRequestId, documentNamesId });
			commit.SET_ESIGN_TEMPLATE_STATUSES({ documentRequestId, status: response });
			return response;
		},
		async fetchDocusignTemplateStatus(context, { documentRequestId, documentNamesId }): Promise<string> {
			const { commit } = actionContext(context);
			const docusignService = AutoFactory.get(OnlinePortalDocusignApi);
			const response = await docusignService.fetchTemplateStatus({ documentRequestId, documentNamesId });
			commit.SET_ESIGN_TEMPLATE_STATUSES({ documentRequestId, status: response });
			return response;
		},
		async resetTemplateEsignature(context, { customerId, documentRequestId }): Promise<any> {
			const { state } = actionContext(context);

			if (state.signatureType === SignatureType.DOCUSIGN) {
				const docusignService = AutoFactory.get(OnlinePortalDocusignApi);
				const response = await docusignService.resetTemplateEsignature({ customerId, documentRequestId });
				return response;
			} else {
				const esignService = AutoFactory.get(OnlinePortalAdobeEsignApi);
				const response = await esignService.resetTemplateEsignature({ customerId, documentRequestId });
				return response;
			}
		},
		async getTemplateUrl(context, { documentRequestId }): Promise<string> {
			const { state } = actionContext(context);

			if (state.signatureType === SignatureType.DOCUSIGN) {
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const docusignService = AutoFactory.get(OnlinePortalDocusignApi);
				// eslint-disable-next-line no-console
				const response = await docusignService.getTemplateSigningUrl(documentRequestId);
				return response;
			} else {
				const esignService = AutoFactory.get(OnlinePortalAdobeEsignApi);
				const response = await esignService.getTemplateSigningUrl(documentRequestId);
				return response;
			}
		},
		async fetchSignatureType(context, { applicationType }): Promise<SignatureType> {
			const { commit, state } = actionContext(context);

			if (state.signatureType != null) {
				return state.signatureType;
			}

			let signatureType = SignatureType.ADOBE;

			try {
				const thirdPartySettingsService = AutoFactory.get(DynamicApplicationThirdPartySettingsApi);
				const isDocusignEnabled = await thirdPartySettingsService.isDocusignEnabled(applicationType);
				// Check which signature provider is actually enabled for this customer
				signatureType = isDocusignEnabled ? SignatureType.DOCUSIGN : SignatureType.ADOBE;
			} catch (error) {
				// If the endpoint fails, default to Adobe
				signatureType = SignatureType.ADOBE;
			}

			commit.SET_SIGNATURE_TYPE(signatureType);
			return signatureType;
		},
	},
	getters: {
		adobeAgreementCreated: state => {
			return Boolean(state.esignAgreementStatus?.requestedDate);
		},
		adobeRecipientStatuses: state => {
			return state.esignAgreementStatus?.recipientStatuses ?? null;
		},
		allRecipientsHaveSignedAgreement: state => {
			if (state.esignAgreementStatus && state.esignAgreementStatus.recipientStatuses) {
				let recipientsMissingSignature: RecipientSignatureStatus[] = state.esignAgreementStatus.recipientStatuses.filter(rec => {
					return rec.dateSigned === null || rec.dateSigned === undefined || rec.dateSigned === '';
				});

				return recipientsMissingSignature.length === 0;
			}

			return false;
		}
	}
});

export default ESignModule;
